import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Clock, FileText, MoreHorizontal, Users } from 'lucide-react';
import { useBillingNouns } from '@/hooks/useSession';
import { cn } from './cn';
import { SCREEN_SCROLL_ID } from './Screen';

const TABS = [
  { root: '/time', label: 'Time', icon: Clock, match: ['/time'] },
  { root: '/invoices', label: 'Invoices', icon: FileText, match: ['/invoices'] },
  { root: '/clients', label: 'Clients', icon: Users, match: ['/clients'] },
  { root: '/more', label: 'More', icon: MoreHorizontal, match: ['/more'] }
] as const;

/** Where each tab was left, so switching tabs comes back to the same screen. */
const lastPath = new Map<string, string>();

export function tabOf(pathname: string) {
  return TABS.find((t) => t.match.some((m) => pathname === m || pathname.startsWith(`${m}/`)));
}

/**
 * Four tabs, each with its own stack: switching tabs returns to where that tab
 * was; tapping the tab you are on pops it to its root.
 */
export function TabBar() {
  const nouns = useBillingNouns();
  const location = useLocation();
  const navigate = useNavigate();
  const active = tabOf(location.pathname);

  useEffect(() => {
    if (active) lastPath.set(active.root, location.pathname + location.search);
  }, [active, location.pathname, location.search]);

  return (
    <nav className="relative z-40 shrink-0 border-t bg-[var(--bar)] pb-[max(8px,calc(var(--sab)-14px))]">
      {/* Items sit 10pt under the hairline, and the home-indicator inset is
          trimmed by 14pt (never under 8): the full inset under a 49pt row
          left the icons pressed to the top edge over an empty band. */}
      <div className="grid grid-cols-4 pt-2.5 pb-1">
        {TABS.map((t) => {
          const isActive = active?.root === t.root;
          const Icon = t.icon;
          return (
            <button
              key={t.root}
              type="button"
              onClick={() => {
                if (isActive) {
                  if (location.pathname !== t.root) navigate(t.root);
                  else document.getElementById(SCREEN_SCROLL_ID)?.scrollTo({ top: 0, behavior: 'smooth' });
                } else navigate(lastPath.get(t.root) ?? t.root);
              }}
              className={cn(
                'flex flex-col items-center gap-1 text-[10px] font-medium',
                isActive ? 'text-[var(--accent)]' : 'text-[var(--text-subtle)]'
              )}
            >
              <Icon className="size-6" strokeWidth={1.9} />
              {t.root === '/clients' ? nouns.clients : t.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
